/**
 * ENG-5966: shared timezone-resolution rule for scheduled tasks.
 *
 * The write-side counterpart to `resolveEffectiveTimezone` in
 * `prompt-wrapper.ts` (which is read-side, deciding whether to render the
 * [NOW] block). This is the single source of truth for "what IANA timezone
 * should a scheduled-task row be PERSISTED with", consumed by both the
 * host-runtime API endpoints and the webapp agent create/update routes so the
 * inheritance rule can't drift between them.
 */

/**
 * True when a timezone value carries no explicit IANA zone and therefore means
 * "inherit the team default, then fall back to UTC". Covers:
 *  - `undefined` / `null`
 *  - blank / whitespace-only strings
 *  - the literal sentinel `'auto'` — scheduled-task templates ship this
 *    (e.g. the agent-role library's "Hourly Urgent Email Check"); a webapp
 *    path even persisted it verbatim via `task.timezone ?? 'UTC'`
 *  - `'UTC'` itself — the model's clock already reads UTC, so for
 *    date-anchoring it's indistinguishable from "unset"
 */
export function isUnsetTimezone(tz: string | null | undefined): boolean {
  if (!tz) return true;
  const trimmed = tz.trim();
  return (
    trimmed.length === 0 ||
    trimmed.toLowerCase() === 'auto' ||
    trimmed.toUpperCase() === 'UTC'
  );
}

/**
 * Resolve the IANA timezone to PERSIST for a scheduled task.
 *
 * When `requested` is unset (blank / `'auto'` / `'UTC'`), inherit
 * `teamTimezone`; when the team has no usable tz either, fall back to `'UTC'`.
 * An explicit non-UTC `requested` always wins over the team setting.
 *
 * Both arguments are normalized identically, so passing a pre-validated team
 * tz (e.g. the API's `getTeamTimezone()`, which returns `null` for blank/UTC)
 * or a raw `team.settings.timezone` string both behave correctly.
 */
export function resolveScheduledTaskTimezone(
  requested: string | null | undefined,
  teamTimezone: string | null | undefined,
): string {
  if (!isUnsetTimezone(requested)) return requested!.trim();
  if (!isUnsetTimezone(teamTimezone)) return teamTimezone!.trim();
  return 'UTC';
}

/**
 * ENG-6695: resolve the IANA timezone an AGENT should be scheduled / restarted
 * in. Precedence (highest first): the agent's own `timezone` override → the
 * team default (`teams.settings->>'timezone'`) → the org default
 * (`organizations.settings->>'timezone'`) → `'UTC'`.
 *
 * Every tier is normalized via {@link isUnsetTimezone}, so blank / `'auto'` /
 * `'UTC'` at any level transparently falls through to the next — and a null
 * agent timezone reproduces today's team/org-inherited behaviour exactly
 * (backward-compatible).
 *
 * The manager points its disruptive-restart / maintenance-window computation at
 * this so "off-peak" is calculated in the operator's zone, not the org default.
 * (A future `reports_to`-person timezone, once organization_people grows one,
 * slots in as the highest-precedence source ahead of the agent override.)
 */
export function resolveAgentTimezone(
  agentTimezone: string | null | undefined,
  teamTimezone: string | null | undefined,
  orgTimezone: string | null | undefined,
): string {
  if (!isUnsetTimezone(agentTimezone)) return agentTimezone!.trim();
  if (!isUnsetTimezone(teamTimezone)) return teamTimezone!.trim();
  if (!isUnsetTimezone(orgTimezone)) return orgTimezone!.trim();
  return 'UTC';
}
